import { AlertTriangle, Smartphone, MapPin, Radar, ShieldAlert, FileCheck } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, Slab, MockVisual, CTABanner } from '@/components/marketing/primitives.jsx';

const capabilities = [
  {
    icon: Smartphone,
    title: 'Device-attested intake',
    description:
      'Every submission carries a device fingerprint + authenticity score. Silent-activation and duress-pattern reports are flagged for priority routing.',
  },
  {
    icon: Radar,
    title: 'Priority Index triage',
    description:
      'Transparent 4-domain weighting (threat 40%, device 20%, media 25%, context 15%) with national-security elevations and jurisdiction-aware escalation.',
  },
  {
    icon: MapPin,
    title: 'Live dispatch board',
    description:
      'Real-time map + queue for agency dispatch, with unit assignment, status updates, and BOLO coordination over private WebSocket channels.',
  },
  {
    icon: ShieldAlert,
    title: 'Protectee fast-path',
    description:
      'Reports involving federally-protected persons (POTUS, VIP events, 18 USC §3056 class) route directly to USSS and elevate PI automatically.',
  },
  {
    icon: FileCheck,
    title: 'Chain-of-custody evidence',
    description:
      'Media files are hashed at upload, sealed per-report in the integrity chain, and preserved through Vision analysis and agency review.',
  },
  {
    icon: AlertTriangle,
    title: 'BOLO + suspect coordination',
    description:
      'Be-On-The-Lookout records propagate in near-real-time across participating agencies with expiry and revocation signed end-to-end.',
  },
];

const flowSteps = [
  { num: '01', label: 'Citizen submits',     desc: 'Mobile app signs and uploads with device attestation.' },
  { num: '02', label: 'Priority Index',      desc: 'NACIS scores threat, device, media, and context in transparent weights.' },
  { num: '03', label: 'Agency routing',      desc: 'Routing manifest lists receiving agencies; each has a scoped view.' },
  { num: '04', label: 'Dispatch + response', desc: 'Officers see the live map, accept tasks, and update status.' },
  { num: '05', label: 'Signed closure',      desc: 'Disposition is signed; integrity chain is closed; audit is finalised.' },
];

export default function RERRPage() {
  return (
    <>
      <Hero
        eyebrow="RERR — Random Event Reporting & Response"
        title="Civic reports that stand up in court."
        description="Device attestation, priority scoring, agency routing, dispatch, and signed closure — with chain-of-custody at every hand-off."
        primaryCta={{ to: '/for-agencies', label: 'Agency onboarding' }}
        secondaryCta={{ to: '/for-citizens', label: 'For citizens' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Capabilities"
            title="Purpose-built for the full reporting lifecycle."
          />
          <div className="mt-12">
            <FeatureGrid items={capabilities} columns={3} />
          </div>
        </div>
      </section>

      <Slab
        eyebrow="Priority Index"
        title="Triage that is explainable and auditable."
        description="The Priority Index is not a black-box score. Every weight is declared in configuration, every recomputation is logged with its full input delta, and every elevation (national-security, protected-person, silent-activation) is traceable to the rule that fired it."
        bullets={[
          'Threat 40% · Device 20% · Media 25% · Context 15%',
          'National-Security Alert band uplift applied at context scoring',
          'USSS fast-path for reports tagged protected_person_flag',
          'Silent-activation bypasses standard queues with visible banner to reviewers',
        ]}
        image={<MockVisual accent="sky" lines={6} />}
      />

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader eyebrow="Lifecycle" title="From submission to signed closure — five stages." />
          <div className="mt-12 grid gap-6 md:grid-cols-5">
            {flowSteps.map(s => (
              <div key={s.num} className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="text-xs font-semibold tracking-[0.14em] text-sky-600">{s.num}</div>
                <div className="mt-2 text-base font-semibold text-slate-900">{s.label}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Slab
        reverse
        eyebrow="Live dispatch"
        title="Real-time coordination over private channels."
        description="Dispatchers and officers subscribe to scoped WebSocket channels — unit, agency, jurisdiction — with authorisation enforced on every subscription. Latency is measured in milliseconds, not seconds."
        bullets={[
          'Private channels per unit, agency, and jurisdiction',
          'Role and tenant enforcement at subscription time',
          'Replay-safe broadcasts (events are also persisted, not just pushed)',
        ]}
        image={<MockVisual accent="rose" lines={6} />}
      />

      <CTABanner
        title="Bring your agency onto NACIS RERR."
        description="The Interoperability Sandbox mirrors production so your integrations can be fully tested before going live."
        primaryCta={{ to: '/contact', label: 'Talk to our team' }}
      />
    </>
  );
}
